import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AccessGroupIdTag, IdTag, IdTagSearchEvent, LookupIdTag } from 'src/app/features/id-tags/id-tag-model';
import { EnvironmentLoaderService } from './environment-loader.service';
import { PagedResult } from '../models/paging';

@Injectable({
  providedIn: 'root',
})
export class IdTagService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  apiUrl: string;

  constructor() {
    this.apiUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/';
  }

  getIdTags(searchEvent?: IdTagSearchEvent): Observable<PagedResult<IdTag>> {
    const url = `${this.apiUrl}IdTag`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.departmentId) {
      params = params.append('departmentId', searchEvent.departmentId);
    }
    if (searchEvent?.printedId) {
      params = params.append('printedId', searchEvent.printedId);
    }
    if (searchEvent?.status) {
      params = params.append('idTagStatus', searchEvent.status?.toString());
    }
    if (searchEvent?.idTagType) {
      params = params.append('idTagType', searchEvent.idTagType?.toString());
    }
    if (searchEvent?.idTagUserType) {
      params = params.append('idTagUserType', searchEvent.idTagUserType?.toString());
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.chargingUserId) {
      params = params.append('chargingUserId', searchEvent.chargingUserId);
    }
    if (searchEvent?.includeInactive !== null && searchEvent?.includeInactive !== undefined) {
      params = params.append('includeInactive', searchEvent.includeInactive);
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }
    if (searchEvent?.excludedAccessGroupIdentifier) {
      params = params.append('excludedAccessGroupIdentifier', searchEvent.excludedAccessGroupIdentifier);
    }

    return this.http.get<PagedResult<IdTag>>(url, {
      params,
    });
  }

  createIdTag(request: IdTagCreateRequest): Observable<number> {
    const url = this.apiUrl + 'IdTag/';

    return this.http.post<number>(url, request);
  }

  getIdTag(idTagId: number): Observable<IdTag> {
    const url = `${this.apiUrl}IdTag/${idTagId}`;
    return this.http.get<IdTag>(url);
  }

  lookupIdTags(printedId: number): Observable<LookupIdTag[]> {
    const url = `${this.apiUrl}IdTag/lookup?printedId=${printedId}`;
    return this.http.get<LookupIdTag[]>(url);
  }

  enrichIdTag(tagId: number, request: IdTagEnrichRequest): Observable<number> {
    const url = `${this.apiUrl}IdTag/${tagId}/enrich`;
    return this.http.put<number>(url, request);
  }

  editIdTag(id: number, idTag: IdTagEditRequest): Observable<number> {
    const url = `${this.apiUrl}IdTag/${id}`;

    return this.http.put<number>(url, idTag);
  }

  inactivateAndCreate(idTagId: number, request: inactivateAndCreateRequest): Observable<number> {
    const url = `${this.apiUrl}IdTag/${idTagId}/inactivateAndCreate`;
    return this.http.put<number>(url, request);
  }

  setStatus(idTag: IdTag): Observable<number> {
    const url = `${this.apiUrl}IdTag/${idTag.id}/setStatus`;
    return this.http.put<number>(url, { status: idTag.status });
  }
  convertToFleetIdTagForm(id: number, request: ConvertToFleetIdTagRequest): Observable<void> {
    const url = `${this.apiUrl}IdTag/${id}/convertToFleetIdTag`;
    return this.http.put<void>(url, request);
  }

  deleteIdTag(idTagId: number) {
    const url = `${this.apiUrl}IdTag/${idTagId}`;
    return this.http.delete(url);
  }

  getAccessGroups(idTagId: number): Observable<PagedResult<AccessGroupIdTag>> {
    const url = `${this.apiUrl}id-tag/${idTagId}/access-groups`;
    return this.http.get<PagedResult<AccessGroupIdTag>>(url);
  }

  saveAccessGroupToIdTag(idTagId: number, accessGroupIdentifier: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}id-tag/${idTagId}/access-group`, {
      accessGroupIdentifier: accessGroupIdentifier,
    });
  }

  deleteAccessGroupFromIdTag(idTagId: number, accessGroupIdentifier: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}id-tag/${idTagId}/access-group/${accessGroupIdentifier}`);
  }
}

export class inactivateAndCreateRequest {
  departmentId!: number;
  agreementNumber!: number;
}

export class IdTagEnrichRequest {
  IdToken!: string;
  PrintedId!: number;
  description!: string;
  accessGroupIdentifiers!: string[];
  agreementNumber!: number | null;
}

export class IdTagCreateRequest {
  departmentId!: number;
  description?: string;
  idToken?: string | null;
  externalReference?: string;
  darId?: string;
  shippingAddress?: string;
  printedId?: number | null;
  attention?: string;
  agreementNumber!: number;
}

export class IdTagEditRequest {
  departmentId!: number;
  description?: string;
  shippingAddress?: string;
  darId?: string;
  externalReference?: string;
  attention?: string;
  agreementNumber!: number;
}

export class ConvertToFleetIdTagRequest {
  departmentId!: number;
  agreementNumber!: number;
  accessGroupIdentifiers?: string[];
}
